import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, LoginRequest, RegisterRequest, AuthResponse, RegistrationResponse, EmailVerificationRequest, EmailVerificationResponse, ResendVerificationRequest, ResendVerificationResponse, ForgotPasswordRequest, ForgotPasswordResponse, ResetPasswordRequest, ResetPasswordResponse, VerifyResetTokenRequest, VerifyResetTokenResponse, APIError } from '../../types/api';
import { apiClient } from '../../services/api';
import type { RootState } from '../index';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  // Email verification state
  registrationEmail: string | null;
  requiresVerification: boolean;
  verificationMessage: string | null;
  justVerified: boolean; // Track if user just verified their email
  // Password reset state
  passwordResetEmail: string | null;
  passwordResetSent: boolean;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  accessToken: localStorage.getItem('access_token'),
  refreshToken: localStorage.getItem('refresh_token'),
  // Email verification state
  registrationEmail: null,
  requiresVerification: false,
  verificationMessage: null,
  justVerified: false,
  // Password reset state
  passwordResetEmail: null,
  passwordResetSent: false,
};

// Async thunks
export const login = createAsyncThunk<
  AuthResponse,
  LoginRequest,
  { rejectValue: APIError }
>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await apiClient.login(credentials);
      return response;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const register = createAsyncThunk<
  RegistrationResponse,
  RegisterRequest,
  { rejectValue: APIError }
>(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await apiClient.register(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const logout = createAsyncThunk<
  void,
  void,
  { rejectValue: APIError }
>(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await apiClient.logout();
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const loadUser = createAsyncThunk<
  User,
  void,
  { rejectValue: APIError }
>(
  'auth/loadUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await apiClient.getProfile();
      return user;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const updateProfile = createAsyncThunk<
  User,
  Partial<User>,
  { rejectValue: APIError }
>(
  'auth/updateProfile',
  async (userData, { rejectWithValue }) => {
    try {
      const user = await apiClient.updateProfile(userData);
      return user;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const verifyEmail = createAsyncThunk<
  EmailVerificationResponse,
  EmailVerificationRequest,
  { rejectValue: APIError }
>(
  'auth/verifyEmail',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiClient.verifyEmail(data);
      return response;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const resendVerificationEmail = createAsyncThunk<
  ResendVerificationResponse,
  ResendVerificationRequest,
  { rejectValue: APIError }
>(
  'auth/resendVerificationEmail',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiClient.resendVerificationEmail(data);
      return response;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const forgotPassword = createAsyncThunk<
  ForgotPasswordResponse,
  ForgotPasswordRequest,
  { rejectValue: APIError }
>(
  'auth/forgotPassword',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiClient.forgotPassword(data);
      return response;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const resetPassword = createAsyncThunk<
  ResetPasswordResponse,
  ResetPasswordRequest,
  { rejectValue: APIError }
>(
  'auth/resetPassword',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiClient.resetPassword(data);
      return response;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const verifyResetToken = createAsyncThunk<
  VerifyResetTokenResponse,
  VerifyResetTokenRequest,
  { rejectValue: APIError }
>(
  'auth/verifyResetToken',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiClient.verifyResetToken(data);
      return response;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearVerificationState: (state) => {
      state.registrationEmail = null;
      state.requiresVerification = false;
      state.verificationMessage = null;
    },
    clearPasswordResetState: (state) => {
      state.passwordResetEmail = null;
      state.passwordResetSent = false;
    },
    setCredentials: (state, action: PayloadAction<{ user: User; accessToken: string; refreshToken: string }>) => {
      const { user, accessToken, refreshToken } = action.payload;
      state.user = user;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      state.error = null;
      // Clear verification state when successfully authenticated
      state.registrationEmail = null;
      state.requiresVerification = false;
      state.verificationMessage = null;
      state.justVerified = false;
    },
    clearCredentials: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      // Clear verification state on logout
      state.registrationEmail = null;
      state.requiresVerification = false;
      state.verificationMessage = null;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    setJustVerified: (state, action: PayloadAction<boolean>) => {
      state.justVerified = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        state.isAuthenticated = true;
        state.error = null;
        // Clear justVerified flag after successful login
        state.justVerified = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || 'Login failed';
        state.isAuthenticated = false;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.verificationMessage = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Store registration info for verification
        state.registrationEmail = action.payload.email;
        state.requiresVerification = action.payload.requires_verification;
        state.verificationMessage = action.payload.message;
        // Don't set authentication - user needs to verify email first
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || 'Registration failed';
        state.isAuthenticated = false;
        state.registrationEmail = null;
        state.requiresVerification = false;
        state.verificationMessage = null;
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        // Even if logout fails on server, clear local state
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.error = action.payload?.detail || 'Logout failed';
      })
      // Load User
      .addCase(loadUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loadUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || 'Failed to load user';
        // Don't clear authentication on load user failure
        // This could be a temporary network issue
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || 'Failed to update profile';
      })
      // Email Verification
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.verificationMessage = action.payload.message;
        // Set justVerified flag to show success message on login page
        state.justVerified = true;
        // Clear verification state - user should login now
        state.registrationEmail = null;
        state.requiresVerification = false;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || 'Email verification failed';
      })
      // Resend Verification Email
      .addCase(resendVerificationEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resendVerificationEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.verificationMessage = action.payload.message;
      })
      .addCase(resendVerificationEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || 'Failed to resend verification email';
      })
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.passwordResetSent = false;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.passwordResetSent = true;
        // Note: We don't store the email for security, component should track it locally
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || 'Failed to send reset email';
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Password reset successful - don't auto-login, let user sign in
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || 'Failed to reset password';
      })
      // Verify Reset Token
      .addCase(verifyResetToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyResetToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Token is valid - component will handle the response
      })
      .addCase(verifyResetToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || 'Invalid or expired reset token';
      });
  },
});

export const { clearError, clearVerificationState, clearPasswordResetState, setCredentials, clearCredentials, updateUser, setJustVerified } = authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectAuth = (state: RootState) => state.auth;
export const selectIsAuthenticated = (state: RootState) => state.auth?.isAuthenticated || false;
export const selectUser = (state: RootState) => state.auth?.user || null;
export const selectAuthLoading = (state: RootState) => state.auth?.isLoading || false;
export const selectAuthError = (state: RootState) => state.auth?.error || null;
export const selectRegistrationEmail = (state: RootState) => state.auth?.registrationEmail || null;
export const selectRequiresVerification = (state: RootState) => state.auth?.requiresVerification || false;
export const selectVerificationMessage = (state: RootState) => state.auth?.verificationMessage || null;
export const selectJustVerified = (state: RootState) => state.auth?.justVerified || false;
export const selectPasswordResetSent = (state: RootState) => state.auth?.passwordResetSent || false;